import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Filter from '../Filter/Filter'
import API from '../../api/api'
import Loader from '../Loader/Loader'

// Dormakaba Community REST v2 authenticates with an OAuth password grant against
// tokenHost, then calls host — so a viability check needs the full credential set.
//
// protocol/tokenProtocol default to https but are pickable: on-prem Community
// servers are commonly reachable over plain http on a separate port, and the two
// can differ from each other (one customer serves REST on :6120 http and the
// identity server on :6121 https).
//
// Note: the visible strings live in the catalog and are referenced here by key, then
// resolved with t() at render — a module-level const can't call a hook. The extractor
// runs with `removeUnusedKeys: false` (see i18next.config.ts), so keys reached only
// through a dynamic t() like this are preserved rather than pruned.
const DORMAKABA_FIELDS = [
  {
    key: 'host',
    labelKey: 'connection-testing:dormakaba.host.label',
    placeholderKey: 'connection-testing:dormakaba.host.placeholder',
  },
  {
    key: 'protocol',
    labelKey: 'connection-testing:dormakaba.protocol.label',
    optional: true,
    options: ['https', 'http'],
  },
  {
    key: 'tokenHost',
    labelKey: 'connection-testing:dormakaba.tokenHost.label',
    optional: true,
    placeholderKey: 'connection-testing:dormakaba.tokenHost.placeholder',
  },
  {
    key: 'tokenProtocol',
    labelKey: 'connection-testing:dormakaba.tokenProtocol.label',
    optional: true,
    options: ['https', 'http'],
  },
  { key: 'clientId', labelKey: 'connection-testing:dormakaba.clientId.label' },
  {
    key: 'clientSecret',
    labelKey: 'connection-testing:dormakaba.clientSecret.label',
    type: 'password',
  },
  { key: 'username', labelKey: 'connection-testing:dormakaba.username.label' },
  { key: 'password', labelKey: 'connection-testing:dormakaba.password.label', type: 'password' },
  { key: 'scope', labelKey: 'connection-testing:dormakaba.scope.label' },
  {
    key: 'requesterId',
    labelKey: 'connection-testing:dormakaba.requesterId.label',
    optional: true,
    placeholderKey: 'connection-testing:dormakaba.requesterId.placeholder',
  },
]

const ConnectionTesting = () => {
  const { t } = useTranslation(['connection-testing'])
  const [integration, setIntegration] = useState(null)
  const [ipAddress, setIpAddress] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [dormakaba, setDormakaba] = useState({ protocol: 'https', tokenProtocol: 'https' })
  const [isTesting, setIsTesting] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [statusType, setStatusType] = useState(null) // 'success' | 'error' | null
  const requestHistory = useRef({})

  const integrationOptions = [
    { id: 'dormakaba', text: 'Dormakaba' },
    { id: 'salto-space', text: 'Salto Space' },
  ]
  const isDormakaba = integration === 'dormakaba'
  const isDisabled = isDormakaba
    ? DORMAKABA_FIELDS.some((field) => !field.optional && !dormakaba[field.key])
    : !integration || !ipAddress || !apiKey

  const getIntegrationText = (id) => {
    const option = integrationOptions.find((integration) => integration.id === id)
    return option ? option.text : t('connection-testing:selectIntegrationPlaceholder')
  }

  const getRateLimitKey = () => `${integration}-${isDormakaba ? dormakaba.host : ipAddress}`

  const isRateLimited = () => {
    const key = getRateLimitKey()
    const now = Date.now()
    const windowMs = 60 * 1000
    const maxAttempts = 10

    if (!requestHistory.current[key]) {
      requestHistory.current[key] = []
    }

    requestHistory.current[key] = requestHistory.current[key].filter((ts) => ts > now - windowMs)

    if (requestHistory.current[key].length >= maxAttempts) {
      return true
    }

    requestHistory.current[key].push(now)
    return false
  }

  const setDormakabaField = (key, value) =>
    setDormakaba((previous) => ({ ...previous, [key]: value }))

  const handleTest = async () => {
    setIsTesting(true)
    setStatusMessage('')
    setStatusType(null)

    if (isDisabled) {
      setStatusMessage(t('connection-testing:fieldsRequiredError'))
      setStatusType('error')
      setIsTesting(false)
      return
    }
    if (isRateLimited()) {
      setStatusMessage(t('connection-testing:rateLimitedError'))
      setStatusType('error')
      setIsTesting(false)
      return
    }

    try {
      let result
      if (integration === 'salto-space') {
        result = await API.testSaltoSpaceIntegration({ url: ipAddress, apiKey })
      } else if (isDormakaba) {
        result = await API.testDormakabaIntegration(dormakaba)
      }

      if (result?.data?.success || result.status === 200) {
        setStatusMessage(t('connection-testing:connectionAvailable'))
        setStatusType('success')
      } else {
        console.error('Connection test failed:', result)
        setStatusMessage(t('connection-testing:connectionFailed'))
        setStatusType('error')
      }
    } catch (error) {
      console.error('Connection test error:', error)
      setStatusMessage(error?.response?.data?.message || t('connection-testing:connectionFailed'))
      setStatusType('error')
    } finally {
      setIsTesting(false)
    }
  }

  return (
    <div className="ctp-connection-testing">
      <div className="ctp-connection-testing__header">
        <Filter
          variant="rounded"
          placeholder={getIntegrationText(integration)}
          name="Integration"
          additionalClass="integration-filter building-filter"
          options={integrationOptions}
          value={integration ? [{ id: integration, text: getIntegrationText(integration) }] : []}
          onChange={(e) => setIntegration(e.target.value.id)}
          closeAfterSelect={true}
        />
        {isDormakaba ? (
          DORMAKABA_FIELDS.map((field) => (
            <div className="ctp-connection-testing__item" key={field.key}>
              <label className="ctp-connection-testing__label">{t(field.labelKey)}</label>
              {field.options ? (
                <select
                  className="input"
                  value={dormakaba[field.key]}
                  onChange={(e) => setDormakabaField(field.key, e.target.value)}
                >
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="input"
                  type={field.type || 'text'}
                  value={dormakaba[field.key] || ''}
                  onChange={(e) => setDormakabaField(field.key, e.target.value.trim())}
                  placeholder={field.placeholderKey ? t(field.placeholderKey) : t(field.labelKey)}
                />
              )}
            </div>
          ))
        ) : (
          <>
            <div className="ctp-connection-testing__item">
              <label className="ctp-connection-testing__label">
                {t('connection-testing:ipAddressLabel')}
              </label>
              <input
                className="input"
                type="text"
                value={ipAddress}
                onChange={(e) => setIpAddress(e.target.value.trim())}
                placeholder={t('connection-testing:ipAddressPlaceholder')}
              />
            </div>
            <div className="ctp-connection-testing__item">
              <label className="ctp-connection-testing__label">
                {t('connection-testing:apiKeyLabel')}
              </label>
              <input
                className="input"
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value.trim())}
                placeholder={t('connection-testing:apiKeyPlaceholder')}
              />
            </div>
          </>
        )}

        <button
          type="button"
          className={`button button-primary ${isTesting ? 'button-disabled' : ''}`}
          onClick={handleTest}
          disabled={isDisabled}
        >
          {t('connection-testing:testButton')}
        </button>
        {isTesting && (
          <div className="loader-wrapper">
            <Loader />
          </div>
        )}
      </div>

      {statusMessage && (
        <div
          className={`ctp-connection-testing__status ${
            statusType === 'success'
              ? 'ctp-connection-testing__status--success'
              : 'ctp-connection-testing__status--error'
          }`}
        >
          {statusMessage}
        </div>
      )}
    </div>
  )
}

export default ConnectionTesting
